import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import CustomTextInputBasic from '../../components/common/CustomTextInput/CustomTextInputBasic';
import Grid from '../../components/layout/grid/Grid';
import CustomButton from '../../components/common/CustomButton/CustomButton';
import { createCourseModule, updateCourseModule } from '../../utils/urls';
import { toast } from 'react-toastify';
import axios from 'axios';

const EditModuleDialogContent = ({
    editingModule,
    onSubmit,
    courseId,
    onUpdateModule,
    isAddMode,
    onClose,
    formikRef,
}) => {
    const [selectedFile, setSelectedFile] = useState(null);

    const initialValues = {
        id: editingModule?.id || '',
        title: editingModule?.title || '',
        file_type: editingModule?.file_type || '',
        file: null,
        file_url: editingModule?.file_url || '',
        content: editingModule?.content || '',
        status: editingModule?.status || false,
    };

    useEffect(() => {
        if (formikRef?.current) {
            formikRef.current.resetForm({ values: initialValues });
        }
        setSelectedFile(null);
    }, [editingModule]);

    const validationSchema = Yup.object().shape({
        title: Yup.string().required('Module title is required'),
        file_type: Yup.string().required('File type is required'),
    });

    const handleModuleCreate = async (values, { setSubmitting }) => {
        try {
            const moduleFormData = new FormData();
            moduleFormData.append('course_id', courseId);
            moduleFormData.append('title', values.title || '');
            moduleFormData.append('file_type', values.file_type);

            if (values.file_type === 'Text' || values.file_type === 'URL') {
                moduleFormData.append('content', values.content || '');
            } else {
                if (values.file) {
                    moduleFormData.append('file', values.file);
                }
            }

            await axios.post(createCourseModule, moduleFormData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            toast.success('Module saved successfully.');
            onClose();
            if (onUpdateModule) onUpdateModule();
        } catch (error) {
            console.error('Error saving course module', error);
            toast.error('Failed to save module');
        } finally {
            setSubmitting(false);
        }
    };

    const handleModuleUpdate = async (updatedModule) => {
        try {
            const formData = new FormData();

            for (const key in updatedModule) {
                if (updatedModule[key] !== null && updatedModule[key] !== undefined) {
                    if (key === "file" && updatedModule.file instanceof File) {
                        formData.append("file", updatedModule.file);
                    } else {
                        formData.append(key, updatedModule[key]);
                    }
                }
            }

            await axios.put(updateCourseModule, formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            });

            toast.success("Module updated successfully.");
            onClose();
            if (onUpdateModule) onUpdateModule();
        } catch (error) {
            console.error("Error updating course module", error);
        }
    };

    return (
        <Formik
            innerRef={formikRef}
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={editingModule ? handleModuleUpdate : handleModuleCreate}
        >
            {({ values, handleChange, setFieldValue }) => (
                <Form className="m-4">
                    <Grid gap={24}>
                        {/* Module Name */}
                        <Grid item xs={12} md={6}>
                            <label className="block text-sm font-medium mb-1">
                                Module Name <span className="text-red-500">*</span>
                            </label>
                            <CustomTextInputBasic
                                name="title"
                                value={values.title}
                                onChange={handleChange}
                                placeholder="Enter"
                            />
                        </Grid>

                        {/* File Type */}
                        <Grid item xs={12} md={6}>
                            <label className="block text-sm font-medium mb-1">
                                Select File Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="file_type"
                                value={values.file_type}
                                onChange={(e) => {
                                    handleChange(e);
                                    setFieldValue('file', null);
                                    setFieldValue('file_url', '');
                                    setFieldValue('content', '');
                                    setSelectedFile(null);
                                }}
                                className="w-full border border-gray-300 rounded-lg p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select</option>
                                <option value="Text">Text</option>
                                <option value="PDF">PDF</option>
                                <option value="Video">Video</option>
                                <option value="URL">URL</option>
                            </select>
                        </Grid>

                        {/* File Upload Section */}
                        <Grid item xs={12} className="flex gap-4">
                            {/* Upload File (Video/PDF) */}
                            <div className="flex flex-1 flex-col">
                                <label className="text-sm font-medium text-gray-700 mb-2">
                                    Uploaded File <span className="text-red-500">*</span>
                                </label>
                                <div className="p-4 border border-gray-300 rounded-xl bg-white shadow-sm min-h-[200px]">
                                    {(values.file_type === 'PDF' || values.file_type === 'Video') ? (
                                        <>
                                            {values.file_url && !selectedFile ? (
                                                <div className="flex flex-col gap-4">
                                                    <div className="flex items-center flex-col gap-6">
                                                        {values.file_type === 'Video' ? (
                                                            <video
                                                                src={values.file_url}
                                                                controls
                                                                className="w-full h-32 rounded-md object-cover border border-gray-200"
                                                            />
                                                        ) : (
                                                            <embed
                                                                src={values.file_url}
                                                                type="application/pdf"
                                                                className="w-full h-32 rounded-md border border-gray-200"
                                                            />
                                                        )}
                                                        <div className="flex gap-3">
                                                            <CustomButton
                                                                type="button"
                                                                onClick={() => document.getElementById('fileInput').click()}
                                                                label="Re-upload"
                                                                variant="contained"
                                                                className="bg-blue-600 text-white hover:bg-blue-700 transition-all"
                                                            />
                                                            <CustomButton
                                                                type="button"
                                                                onClick={() => {
                                                                    setFieldValue('file_url', '');
                                                                    setFieldValue('file', null);
                                                                    setSelectedFile(null);
                                                                }}
                                                                label="Remove"
                                                                variant="outlined"
                                                                className="border border-gray-300 text-gray-700 hover:bg-gray-100 transition-all"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : selectedFile ? (
                                                <div className="flex items-center flex-col justify-between gap-6">
                                                    {values.file_type === 'Video' ? (
                                                        <video
                                                            src={URL.createObjectURL(selectedFile)}
                                                            controls
                                                            className="w-full h-32 rounded-md object-cover border border-gray-200"
                                                        />
                                                    ) : (
                                                        <embed
                                                            src={URL.createObjectURL(selectedFile)}
                                                            type="application/pdf"
                                                            className="w-full h-32 rounded-md border border-gray-200"
                                                        />
                                                    )}
                                                    <div className="flex gap-3">
                                                        <CustomButton
                                                            type="button"
                                                            onClick={() => {
                                                                setFieldValue('file', selectedFile);
                                                                setFieldValue('file_url', URL.createObjectURL(selectedFile));
                                                                setSelectedFile(null);
                                                            }}
                                                            label="Upload"
                                                            variant="contained"
                                                            className="bg-blue-600 text-white hover:bg-blue-700 transition-all"
                                                        />
                                                        <CustomButton
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedFile(null);
                                                            }}
                                                            label="Cancel"
                                                            variant="outlined"
                                                            className="border border-gray-300 text-gray-700 hover:bg-gray-100 transition-all"
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <label
                                                    htmlFor="fileInput"
                                                    className="h-full flex flex-col items-center justify-center px-6 py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-blue-400 transition-all cursor-pointer"
                                                >
                                                    <svg
                                                        className="w-10 h-10 text-gray-400 mb-2"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="1.5"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-8m0 0l-3 3m3-3l3 3m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <p className="text-sm text-gray-500">
                                                        Click to upload your {values.file_type === 'PDF' ? 'PDF' : 'video'} file
                                                    </p>
                                                    <input
                                                        id="fileInput"
                                                        type="file"
                                                        accept={values.file_type === 'PDF' ? 'application/pdf' : 'video/*'}
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) setSelectedFile(file);
                                                        }}
                                                        className="hidden"
                                                    />
                                                </label>
                                            )}
                                        </>
                                    ) : (
                                        <div className="h-full p-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                                            <input
                                                type="file"
                                                disabled
                                                className="w-full text-gray-400 bg-gray-50 cursor-not-allowed"
                                                placeholder="Upload not available for this file type"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Text Area */}
                            <div className="flex flex-1 flex-col">
                                <label className="block text-sm font-medium mb-1">
                                    Text <span className="text-red-500">*</span>
                                </label>
                                <div className="flex flex-1 items-center gap-2 p-4 border border-gray-300 rounded-lg shadow-sm bg-gray-100">
                                    <textarea
                                        name="content"
                                        value={values.file_type === 'Text' ? values.content : ''}
                                        onChange={handleChange}
                                        disabled={values.file_type !== 'Text'}
                                        placeholder="Enter content here..."
                                        rows={4}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${values.file_type === 'Text' ? '' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            }`}
                                    />
                                </div>
                            </div>

                            {/* URL Area */}
                            <div className="flex flex-1 flex-col">
                                <label className="block text-sm font-medium mb-1">
                                    Link <span className="text-red-500">*</span>
                                </label>
                                <div className="flex flex-1 items-center gap-2 p-4 border border-gray-300 rounded-lg shadow-sm bg-gray-100">
                                    <textarea
                                        name="content"
                                        value={values.file_type === 'URL' ? values.content : ''}
                                        onChange={handleChange}
                                        disabled={values.file_type !== 'URL'}
                                        placeholder="Enter URL here..."
                                        rows={4}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${values.file_type === 'URL' ? '' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            }`}
                                    />
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    );
};

export default EditModuleDialogContent;
